var _a;
import { isCstNode, traverse, } from '../helpers/chevrotain/cst';
import { sparqlTokenMap } from '../sparql/tokens';
// RegEx for matching any relevant children of `Expression` inside of `Bind`;
// used to avoid false negatives in the check for disallowed literals inside of
// `Bind`.
var subExpressionMatcher = /(?:[A-Z]+Expression|ValueLogical)$/i;
// Default: just don't abort early at all. Used in the stack unwinding process
// that creates an error rule stack.
var defaultEarlyAbortTest = function () { return false; };
// Tokens that are allowed in SPARQL but not inside the `IfClause` of SRS.
var disallowedSparqlTokenNameToRuleMap = (_a = {},
    _a[sparqlTokenMap.EXISTS.tokenName] = 'ExistsFunction',
    _a[sparqlTokenMap.NOT_EXISTS.tokenName] = 'NotExistsFunction',
    _a[sparqlTokenMap.NOW.tokenName] = 'BuiltInCall_NOW',
    _a[sparqlTokenMap.LEmbed.tokenName] = 'LeftEmbed',
    _a[sparqlTokenMap.REmbed.tokenName] = 'RightEmbed',
    _a);
var disallowedSparqlTokenNames = Object.keys(disallowedSparqlTokenNameToRuleMap);
// Token names for literals; these are not allowed in the subject position of
// certain patterns in SRS.
var disallowedSparqlLiteralTokenNames = [
    sparqlTokenMap.DOUBLE,
    sparqlTokenMap.DECIMAL,
    sparqlTokenMap.INTEGER,
    sparqlTokenMap.DOUBLE_POSITIVE,
    sparqlTokenMap.DECIMAL_POSITIVE,
    sparqlTokenMap.INTEGER_POSITIVE,
    sparqlTokenMap.DOUBLE_NEGATIVE,
    sparqlTokenMap.DECIMAL_NEGATIVE,
    sparqlTokenMap.INTEGER_NEGATIVE,
    sparqlTokenMap.STRING_LITERAL1,
    sparqlTokenMap.STRING_LITERAL2,
    sparqlTokenMap.STRING_LITERAL_LONG1,
    sparqlTokenMap.STRING_LITERAL_LONG2,
].map(function (token) { return token.tokenName; });
// Helper functions for more "literate" code.
var isCstNodeTraverseContext = function (ctx) {
    return Boolean(ctx && ctx.node);
};
// Is the parent node an Expression child of a Bind node or an Expression
// child of an ExpressionOrEmbeddedTriplePattern node that is itself a child
// of a Bind node? The former accounts for standard SPARQL; the latter accounts
// for Stardog SPARQL with edge properties. This lets certain custom errors
// work with the output of either the W3CSpecSparqlParser or the
// StardogSparqlParser.
var isParentBindOrBoundExpressionOrEmbeddedTriplePattern = function (parentCtx) {
    if (!isCstNodeTraverseContext(parentCtx)) {
        return false;
    }
    var parentNodeName = parentCtx.node.name;
    return (parentNodeName === 'Bind' ||
        (parentNodeName === 'ExpressionOrEmbeddedTriplePattern' &&
            isCstNodeTraverseContext(parentCtx.parentCtx) &&
            parentCtx.parentCtx.node.name === 'Bind'));
};
// Walks back up the tree to construct the rule stack, first going upward
// through the provided `traverseCtx`, and then continuing up through the
// `fullCtx`. `traverseCtx` is intended to be the "inner" ITraverseContext
// representing the results of the SPARQL sub-parser used by the
// SparqlSrsVisitor. `fullCtx` is intended to be the "outer" ITraverseContext
// representing the results of the SRS parser itself. The SRS parser delegates
// blocks of SPARQL to a SPARQL sub-parser, so, by combinging the two contexts,
// we get the full parser stack. The traversal adds rules to the stack only
// once a rule matching one of the `startRuleNames` is hit.
//
// At the point where the traversal of `traverseCtx` ends and the traversal of
// `fullCtx` begins, you may need to insert a rule into the stack (e.g.,
// because the `traverseCtx` doesn't include the top-level rule for that
// parse). If so, use `topLevelSubParserRuleName`.
//
// In some cases, there is a need to track nodes and potentially bail out early
// at certain points while constructing the rule stack. For that, use
// `earlyAbortTest`. If that method returns true, the rule stack construction
// will abort.
function getCustomErrorRuleStack(traverseCtx, fullCtx, startRuleNames, topLevelSubParserRuleName, earlyAbortTest) {
    if (earlyAbortTest === void 0) { earlyAbortTest = defaultEarlyAbortTest; }
    if (!traverseCtx) {
        return []; // forced early exit
    }
    var ruleStack = [];
    var stackUnwindingPointer = traverseCtx;
    // Move up from current context to the first rule that should "start" the stack.
    while (isCstNode(stackUnwindingPointer.node) &&
        !startRuleNames.includes(stackUnwindingPointer.node.name)) {
        if (earlyAbortTest(stackUnwindingPointer)) {
            return [];
        }
        stackUnwindingPointer = stackUnwindingPointer.parentCtx;
    }
    // Now start adding all found rules to the stack as we move upward.
    while (isCstNode(stackUnwindingPointer.node)) {
        ruleStack.unshift(stackUnwindingPointer.node.name);
        if (earlyAbortTest(stackUnwindingPointer)) {
            return [];
        }
        stackUnwindingPointer = stackUnwindingPointer.parentCtx;
    }
    // If the rule stack of the sub-parser doesn't get all the way up to the
    // relevant top-level rule, this will force the top-level rule to be put onto
    // the stack before proceeding.
    if (typeof topLevelSubParserRuleName === 'string') {
        ruleStack.unshift(topLevelSubParserRuleName);
    }
    // Now that we've got the sub-parser's rule stack, we trace the remaining
    // outer parser's stack to get to the true bottom of the stack.
    stackUnwindingPointer = fullCtx;
    while (stackUnwindingPointer) {
        if (isCstNode(stackUnwindingPointer.node)) {
            ruleStack.unshift(stackUnwindingPointer.node.name);
            if (earlyAbortTest(stackUnwindingPointer)) {
                return [];
            }
        }
        stackUnwindingPointer = stackUnwindingPointer.parentCtx;
    }
    return ['SrsDoc'].concat(ruleStack);
}
var getCustomIRecognitionException = function (_a) {
    var name = _a.name, message = _a.message, node = _a.node, ruleStack = _a.ruleStack;
    return ({
        name: name,
        message: message,
        token: node,
        context: {
            ruleStack: ruleStack,
            // `ruleOccurrenceStack` is meaningless to us as it just
            // records the number used when the chevrotain rule is
            // created (e.g., SUBRULE1 vs SUBRULE2); we can't know that
            // or care about that here
            ruleOccurrenceStack: [],
        },
        resyncedTokens: [],
    });
};
var getNoPrefixError = function (node, parentCtx, fullCtx, subParserRuleName) {
    return getCustomIRecognitionException({
        name: 'NoNamespacePrefixError',
        message: "A prefix (\"" + node.image + "\") was used for which there was no namespace defined.",
        node: node,
        ruleStack: getCustomErrorRuleStack(parentCtx, fullCtx, ['PrefixedName'], subParserRuleName),
    });
};
var getDisallowedTokenError = function (node, parentCtx, fullCtx, subParserRuleName) {
    if (subParserRuleName === void 0) { subParserRuleName = 'GroupGraphPattern'; }
    return getCustomIRecognitionException({
        name: 'DisallowedTokenError',
        message: "Token " + node.tokenType.tokenName + " cannot be used in Stardog Rules.",
        node: node,
        ruleStack: getCustomErrorRuleStack(parentCtx, fullCtx, [disallowedSparqlTokenNameToRuleMap[node.tokenType.tokenName]], subParserRuleName),
    });
};
var getDisallowedLiteralError = function (node, parentCtx, fullCtx, subParserRuleName) {
    var foundPropertyListPathNotEmptyCtx = null;
    var didFindSubExpressionWithMultipleChildren = false;
    var errorContext = null;
    var errorRuleStack = getCustomErrorRuleStack(parentCtx, fullCtx, ['Expression', 'TriplesSameSubjectPath'], subParserRuleName, function (stackCtx) {
        var node = stackCtx.node, parentCtx = stackCtx.parentCtx;
        var nodeName = node.name;
        if (nodeName === 'PropertyListPathNotEmpty') {
            // Track the found `PropertyListPathNotEmmpty` node and keep going.
            foundPropertyListPathNotEmptyCtx = stackCtx;
            return false;
        }
        if (!didFindSubExpressionWithMultipleChildren &&
            subExpressionMatcher.test(nodeName)) {
            // Track that we found a sub-expression with multiple children, then
            // keep going.
            didFindSubExpressionWithMultipleChildren =
                parentCtx.node.children[nodeName].length > 1;
            return false;
        }
        var isExpression = nodeName === 'Expression';
        var isTriplesBlock = nodeName === 'TriplesSameSubjectPath';
        if (!isExpression && !isTriplesBlock) {
            return false;
        }
        var isBoundExpressionWithLiteralSubject = isExpression &&
            // If we've found a sub-expression with multiple children, it's highly
            // likely (maybe definite?) that this `Bind` does not include an invalid
            // literal as a subject, so we don't count this as an error. This _may_
            // allow rare false positives, but it definitely prevents false
            // negatives of the sort described in https://github.com/stardog-union/millan/issues/22
            !didFindSubExpressionWithMultipleChildren &&
            isParentBindOrBoundExpressionOrEmbeddedTriplePattern(parentCtx);
        var isTriplesBlockSubject = isTriplesBlock &&
            (!foundPropertyListPathNotEmptyCtx ||
                foundPropertyListPathNotEmptyCtx.parentCtx.node.name !==
                    'TriplesSameSubjectPath');
        if (isBoundExpressionWithLiteralSubject || isTriplesBlockSubject) {
            errorContext = isBoundExpressionWithLiteralSubject
                ? 'Bind'
                : 'TriplesBlock';
            return false;
        }
        // We got to the Expression or TriplesBlock containing the literal, but
        // the literal wasn't in the subject position (i.e., was not the lead
        // Expression inside of Bind and was not the subject of
        // TriplesSameSubjectPath), so we can bail early here.
        return true;
    });
    if (errorRuleStack.length === 0) {
        return;
    }
    return getCustomIRecognitionException({
        name: 'DisallowedLiteralError',
        message: "Token " + node.tokenType.tokenName + " (" + node.image + ") cannot be used as a subject inside of a " + errorContext + " in Stardog Rules Syntax.",
        node: node,
        ruleStack: errorRuleStack,
    });
};
// Since the SRS parser delegates to the SPARQL parser inside of
// an SRS `IfClause`, and SPARQL allows certain constructs that SRS does not,
// we need to create our own errors for SRS-specific restrictions here.
export function addIfClauseErrorsToErrors(_a) {
    var cst = _a.cst, namespacesMap = _a.namespacesMap, fullCtx = _a.fullCtx, errors = _a.errors, semanticErrors = _a.semanticErrors;
    traverse(cst, function (ctx, next) {
        var node = ctx.node, parentCtx = ctx.parentCtx;
        if (isCstNode(node)) {
            return next();
        }
        var tokenName = node.tokenType.tokenName;
        if (disallowedSparqlTokenNames.some(function (name) { return name === tokenName; })) {
            errors.push(getDisallowedTokenError(node, parentCtx, fullCtx));
        }
        if (disallowedSparqlLiteralTokenNames.some(function (tokenName) { return tokenName === node.tokenType.tokenName; })) {
            var error = getDisallowedLiteralError(node, parentCtx, fullCtx, 'GroupGraphPattern');
            if (error) {
                errors.push(error);
            }
        }
        if (tokenName === 'PNAME_NS' || tokenName === 'PNAME_LN') {
            var prefix = node.image.split(':').shift();
            if (!namespacesMap[prefix]) {
                semanticErrors.push(getNoPrefixError(node, parentCtx, fullCtx, 'GroupGraphPattern'));
            }
        }
    });
    return {
        errors: errors,
        semanticErrors: semanticErrors,
    };
}
export function addThenClauseErrorsToErrors(_a) {
    var cst = _a.cst, namespacesMap = _a.namespacesMap, errors = _a.errors, semanticErrors = _a.semanticErrors, fullCtx = _a.fullCtx;
    traverse(cst, function (ctx, next) {
        var node = ctx.node, parentCtx = ctx.parentCtx;
        if (isCstNode(node)) {
            return next();
        }
        var tokenName = node.tokenType.tokenName;
        if (disallowedSparqlLiteralTokenNames.some(function (tokenName) { return tokenName === node.tokenType.tokenName; })) {
            var error = getDisallowedLiteralError(node, parentCtx, fullCtx, 'GroupGraphPattern');
            if (error) {
                errors.push(error);
            }
        }
        // No embedded triple patterns in SRS THEN clauses.
        if (tokenName === sparqlTokenMap.LEmbed.name ||
            tokenName === sparqlTokenMap.REmbed.name) {
            errors.push(getDisallowedTokenError(node, parentCtx, fullCtx, 'TriplesBlock'));
        }
        if (tokenName === 'PNAME_NS' || tokenName === 'PNAME_LN') {
            var prefix = node.image.split(':').shift();
            if (!namespacesMap[prefix]) {
                semanticErrors.push(getNoPrefixError(node, parentCtx, fullCtx, 'TriplesBlock'));
            }
        }
    });
    return {
        errors: errors,
        semanticErrors: semanticErrors,
    };
}
